import type { TradeItem } from '@/types';
import { aggregateByCurrency, aggregateFromBuckets } from './currency-totals';
import {
  getDisplayedBookedFxRate,
  getDisplayedCobFxRate,
  getEffectiveSettlementCurrency,
  getSettlementAmount,
  getSettlementAmountCcy,
  type SettlementMoneyField,
} from './settlement-ccy';
import { showsClosingCash } from './closing-cash';
import { RATE_LEG_CELLS } from './rate-legs';

/**
 * Monetary columns the CSV export totals (LR-469 → LR-474): per-row accessors,
 * each returning the amount in the currency its `SUMMABLE_BUCKET_CCY` twin
 * reports for that row — usually the EFFECTIVE SETTLEMENT currency (backend
 * `*_converted` twin, else native × booked fx_rate; same-ccy passes through),
 * summed natively per bucket and converted to base only for the grand total.
 * Key and amount move together PER COLUMN — see SUMMABLE_BUCKET_CCY.
 *
 * `daily_earnings` (renamed from daily_earnings_usd, origin/main) sums off its
 * security-ccy native basis like the others; the settlement twin/booked-fx
 * conversion happens inside getSettlementAmount (LR-459).
 *
 * `closing_cash` joined in LR-474: its native basis is confirmed security-ccy
 * (backend `calculate_closing_cash`) and a settlement twin exists (LR-349).
 *
 * `collateral_value` deliberately stays NATIVE on the security-ccy bucket
 * (r2 #2): its cell renders the raw native figure, and for NON-CASH collateral
 * the value may really be in a third (collateral-security) currency — running
 * it through the trade fx would mislabel twice (C3).
 */
/**
 * A row with NO figure in this column must contribute NOTHING — not a zero.
 * `aggregateByCurrency` / `aggregateFromBuckets` skip non-finite values, so `NaN` is
 * how "this row has nothing to say" is expressed; `?? 0` instead added a real zero,
 * which kept the row's currency in the bucket map and made a column nobody has filled
 * in (Last Marked before the first mark of the day, closing cash on a same-ccy book)
 * total `0.00` over a screen of "—". Sums are unchanged wherever at least one row
 * carries a figure — what changes is that a currency appears in the breakdown only if
 * it actually contributed.
 */
const amountOrNothing = (t: TradeItem, field: SettlementMoneyField): number =>
  getSettlementAmount(t, field) ?? NaN;

export const SUMMABLE_COLUMNS: Record<string, (t: TradeItem) => number> = {
  contract_value: (t) => amountOrNothing(t, 'contract_value'),
  collateral_value: (t) => t.collateral_value ?? NaN,
  daily_earnings: (t) => amountOrNothing(t, 'daily_earnings'),
  // NaN, not 0: a GMSLA-only book must print no subtotal, not a false 0.00.
  closing_cash: (t) => (showsClosingCash(t) ? amountOrNothing(t, 'closing_cash') : NaN),
  // LR-522: Trade / Last Marked gross + margin VALUES total on the same settlement
  // basis as Contract Value (their twin family), so a visible value column gets a
  // per-currency subtotal + EUR grand total instead of a blank totals cell. Last
  // Marked values are null until first marked → no contribution at all.
  trade_gross_value: (t) => amountOrNothing(t, 'trade_gross_value'),
  last_marked_gross_value: (t) => amountOrNothing(t, 'last_marked_gross_value'),
  marked_contract_value: (t) => amountOrNothing(t, 'marked_contract_value'),
  // LR-521: COB (T-1) gross/margin VALUES total like Contract Value (their COB
  // twin family) — same settlement basis.
  cob_gross_value: (t) => amountOrNothing(t, 'cob_gross_value'),
  cob_margin_value: (t) => amountOrNothing(t, 'cob_margin_value'),
  // Fee Income / Cash Pool Interest Accrual / Net Amount: native daily figures,
  // bucketed by the security ccy like collateral_value; a dash adds nothing.
  fee_income: (t) => RATE_LEG_CELLS.fee_income(t) ?? NaN,
  cost_of_collateral: (t) => RATE_LEG_CELLS.cost_of_collateral(t) ?? NaN,
  net_amount: (t) => RATE_LEG_CELLS.net_amount(t) ?? NaN,
};

/**
 * LR-546: SUMMABLE_COLUMNS whose whole-book, OPEN-book subtotal the server provides
 * via `GET /positions/metrics/` `column_totals` (backend metrics.py FOOTER_COLUMN_IDS).
 * The blotter footer reads these server subtotals instead of totalling the loaded
 * window. Everything else in SUMMABLE_COLUMNS (daily_earnings / closing_cash =
 * rate-engine, cob_* = COB price) is deliberately NOT summed server-side — those
 * stay client-side (LR-546 accepted limitations) and render "—" in the footer.
 * Keep in lock-step with metrics.py FOOTER_COLUMN_IDS (mirrored by the MSW mock).
 */
export const SERVER_FOOTER_COLUMN_IDS = [
  'contract_value',
  'collateral_value',
  'trade_gross_value',
  'last_marked_gross_value',
  'marked_contract_value',
] as const;

/** SUMMABLE_COLUMNS the server does NOT roll up whole-book (footer renders "—"). */
export const CLIENT_ONLY_FOOTER_COLUMN_IDS: readonly string[] = Object.keys(SUMMABLE_COLUMNS).filter(
  (id) => !(SERVER_FOOTER_COLUMN_IDS as readonly string[]).includes(id),
);

const securityCcy = (t: TradeItem): string => (t.currency || '').toUpperCase();

/**
 * HONEST per-row bucket key paired with each SUMMABLE_COLUMNS accessor (LR-474
 * r1 #1): the key is the currency the accessor's figure is actually in — the
 * settlement ccy when the twin/booked-fx converted it, the SECURITY ccy on the
 * degenerate fallback (cross-ccy legacy payload without twin and fx). Keying
 * unconditionally on `getEffectiveSettlementCurrency` there would file a native
 * figure under a settlement label and roll it up at the wrong spot rate.
 *
 * Known residual (r2 #1-verify): on a payload that has the LR-459 twins but no
 * fx_rate (pre-LR-474 backend, deploy-lag only), twin-backed columns of a trade
 * land in its settlement subtotal while fx-computed columns stay in its
 * security subtotal — each cell is denominationally correct; a single shared
 * key would instead mislabel money.
 *
 * Every SUMMABLE_COLUMNS id MUST have an entry here (parity guarded by test) —
 * the consumers' `?? getEffectiveSettlementCurrency` fallback is a last resort,
 * not a licence to omit the pairing.
 */
export const SUMMABLE_BUCKET_CCY: Record<string, (t: TradeItem) => string> = {
  contract_value: (t) => getSettlementAmountCcy(t, 'contract_value'),
  collateral_value: securityCcy,
  fee_income: securityCcy,
  cost_of_collateral: securityCcy,
  net_amount: securityCcy,
  daily_earnings: (t) => getSettlementAmountCcy(t, 'daily_earnings'),
  closing_cash: (t) => getSettlementAmountCcy(t, 'closing_cash'),
  trade_gross_value: (t) => getSettlementAmountCcy(t, 'trade_gross_value'),
  last_marked_gross_value: (t) => getSettlementAmountCcy(t, 'last_marked_gross_value'),
  marked_contract_value: (t) => getSettlementAmountCcy(t, 'marked_contract_value'),
  cob_gross_value: (t) => getSettlementAmountCcy(t, 'cob_gross_value'),
  cob_margin_value: (t) => getSettlementAmountCcy(t, 'cob_margin_value'),
};

/** Accessors for display/synthetic columns that are NOT plain TradeItem keys.
 *  Data cells stay NATIVE (security-ccy) — C4: the export carries the raw book;
 *  only the totals block below is settlement-based. `settlement_currency`
 *  exports the EFFECTIVE ccy so a same-ccy/legacy row isn't blank (LR-474). */
export const SYNTHETIC_CSV_ACCESSORS: Record<string, (t: TradeItem) => unknown> = {
  settlement_currency: getEffectiveSettlementCurrency,
  // LR-628: the totals row sums this column through `getSettlementAmount`, which
  // now prefers the effective figure — so the DATA cell has to as well, or the
  // column stops footing to its own total (on a same-currency book the two matched
  // exactly before this change). `csv-export` reads `row[colId]` for anything
  // absent from this map, which here would be the booked number.
  daily_earnings: (t) => t.effective_daily_earnings ?? t.daily_earnings,
  // Fee Income / Cash Pool Interest Accrual / Net Amount: the cell's own rule, so
  // a row's legs agree with each other and with Daily Earnings in the file too.
  ...RATE_LEG_CELLS,
  // LR-494 (kvi review #2): COB FX Rate has no native/settlement money duality —
  // its screen value is a pure presentational inversion (1 / stored rate). Export
  // that displayed value so CSV matches the cell/copy (raw `row.cob_fx_rate` would
  // emit the un-inverted 0.8 where the cell/copy show 1.25). Not in SUMMABLE_COLUMNS
  // → it never enters the settlement-based totals block. Blank for same-ccy/no-rate.
  cob_fx_rate: (t) => getDisplayedCobFxRate(t)?.value ?? '',
  // LR-522: Trade FX Rate — same presentational inversion (1 / booked rate);
  // export the displayed value so CSV matches the cell/copy. Blank for same-ccy.
  fx_rate: (t) => getDisplayedBookedFxRate(t) ?? '',
  // Unlike its neighbours `closing_cash` IS a plain TradeItem key — without an
  // entry here `csv-export` reads the raw row and ships the GMSLA number that
  // both the screen and the total withhold. Data cell stays NATIVE (C4).
  closing_cash: (t) => (showsClosingCash(t) ? (t.closing_cash ?? '') : ''),
};

/**
 * Build the appended totals block for a trades CSV export (LR-469): a blank
 * separator row, one native subtotal row per currency, then a single grand-total
 * row converted to `base` (EUR/USD-equivalent) via spot FX. Raw numbers only, so
 * the figures stay machine-summable in a spreadsheet. Never sums different
 * currencies into one native number — only the labelled `≈ base` grand total
 * converts (and is marked `approx` when any rate fell back).
 *
 * Every figure comes from `aggregateByCurrency` — the same primitive the on-page
 * footer uses — so the CSV block and the footer agree to the cent: non-finite
 * values are skipped (not poisoned into NaN), native sums accumulate in integer
 * cents, and each currency bucket converts to base ONCE (not per row, which
 * rounds differently on large books) — kvi review #2/#3.
 *
 * Returns row objects keyed by the SAME column labels as the data rows so
 * Papa.unparse keeps every column aligned.
 *
 * `extraBuckets` (LR-538) carries columns whose per-currency sums the CALLER already
 * computed because a plain `TradeItem` accessor cannot produce them — the MTM export's
 * synthetic New Margin Value / Gross Value Δ / Margin Value Δ depend on the operator's
 * unsaved New Price overrides. They roll up through the same `aggregateFromBuckets`
 * primitive, so the CSV block still agrees with the on-screen footer to the cent.
 */
export function buildCsvTotalsRows(
  data: TradeItem[],
  visibleColumnIds: string[],
  columnLabels: Record<string, string>,
  base = 'EUR',
  extraBuckets?: Record<string, Record<string, number>>,
): Array<Record<string, unknown>> {
  const baseUpper = base.toUpperCase();
  const isSummable = (id: string): boolean =>
    id in SUMMABLE_COLUMNS || extraBuckets?.[id] !== undefined;
  const summableVisible = visibleColumnIds.filter(isSummable);
  if (summableVisible.length === 0 || data.length === 0) return [];

  // Put the row label in the first non-summable column so it can't overwrite a sum.
  // If EVERY visible column is summable there is no safe home for the label — leave it
  // off rather than clobber a numeric total. (The old `?? visibleColumnIds[0]` fell back
  // to a summable column and overwrote its grand-total number with the label string.)
  const labelColId = visibleColumnIds.find((id) => !isSummable(id));
  const labelKey = labelColId != null ? columnLabels[labelColId] : undefined;

  const blankRow = (): Record<string, unknown> => {
    const r: Record<string, unknown> = {};
    for (const id of visibleColumnIds) r[columnLabels[id]] = '';
    return r;
  };

  // One aggregate per column: per-SETTLEMENT-currency sums (D2) + the
  // bucket-once ≈ equivalent. Key and amounts move together — each column pairs
  // its accessor with its honest bucket key (see SUMMABLE_BUCKET_CCY).
  const aggs = new Map(
    summableVisible.map((id) => {
      const pre = extraBuckets?.[id];
      return [
        id,
        pre !== undefined
          ? aggregateFromBuckets(pre, baseUpper)
          : aggregateByCurrency(
              data,
              SUMMABLE_COLUMNS[id],
              baseUpper,
              SUMMABLE_BUCKET_CCY[id] ?? getEffectiveSettlementCurrency,
            ),
      ] as const;
    }),
  );

  // Currencies present in any column (base currency first, then alphabetical). A cell
  // stays blank when that column had no finite contribution in that currency.
  const ccySet = new Set<string>();
  for (const agg of aggs.values()) for (const c of agg.currencies) ccySet.add(c);
  const ccys = [...ccySet].sort((a, b) =>
    a === baseUpper ? -1 : b === baseUpper ? 1 : a.localeCompare(b),
  );
  // Not one visible summable column had a contribution (an unmarked book, or a repo view of a
  // counterparty with no GMRA agreement) → no block at all, rather than a separator plus a
  // "Grand Total" line with nothing on it.
  if (ccys.length === 0) return [];

  const rows: Array<Record<string, unknown>> = [blankRow()];

  for (const ccy of ccys) {
    const row = blankRow();
    if (labelKey != null) row[labelKey] = `Subtotal (${ccy})`;
    for (const id of summableVisible) {
      const value = aggs.get(id)!.byCcy[ccy];
      if (value !== undefined) row[columnLabels[id]] = value;
    }
    rows.push(row);
  }

  const usedFallback = [...aggs.values()].some((a) => a.usedFallback);
  const grand = blankRow();
  for (const id of summableVisible) {
    const agg = aggs.get(id)!;
    // No contribution in ANY currency (a pre-aggregated synthetic column whose every row
    // was skipped) → leave the cell blank. Its per-currency subtotal cells are already
    // blank, so a 0 here would contradict them and the "—" cells in the data rows.
    if (agg.currencies.length === 0) continue;
    grand[columnLabels[id]] = agg.equiv;
  }
  if (labelKey != null) grand[labelKey] = `Grand Total (≈ ${baseUpper}${usedFallback ? ', approx' : ''})`;
  rows.push(grand);

  return rows;
}
